import { EM_DASH, confidenceBand, toPercent } from "../lib/format";

interface Props {
  /** 0-1 or 0-100; normalised internally. null means none was reported. */
  value: number | string | null | undefined;
  /** Explains why there is no score, when there isn't one. */
  absentReason?: string;
}

/**
 * A confidence score with a colour band.
 *
 * The number is always rendered. The dot is decoration, so the badge never
 * relies on colour alone to carry meaning -- which also keeps it readable for
 * the two status steps that sit below 3:1 on the light surface.
 *
 * When there is no score it shows an em dash, never a number. This platform
 * has already shipped a hardcoded 95 standing in for a missing confidence, and
 * on a KYC review screen a plausible number is worse than an honest blank.
 */
export function ConfidenceBadge({ value, absentReason }: Props) {
  const pct = toPercent(value);
  const band = confidenceBand(pct);

  if (pct === null) {
    return (
      <span
        className="badge badge-none"
        title={absentReason ?? "No confidence score was reported for this item"}
      >
        {EM_DASH}
      </span>
    );
  }

  return (
    <span className={`badge badge-${band}`} title={`${band} confidence`}>
      <span className="dot" aria-hidden="true" />
      {pct.toFixed(pct % 1 === 0 ? 0 : 1)}%
    </span>
  );
}
